import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { FormattedDate } from "@/components/Dashboard/FormattedDate";
import { AssignedPostsWidget } from "@/components/Dashboard/AssignedPostsWidget";
import { WebsiteAnalyticsWidget } from "@/components/Dashboard/WebsiteAnalyticsWidget";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Users, CalendarDays, BarChart3, FileCheck, Globe2,
  Ticket, Trophy, FileUp, ChevronRight, Activity,
  TrendingUp, TrendingDown, Minus, Newspaper, Award,
  Zap, Radio, Clock, Star, ArrowUpRight, Share2,
  PenSquare, MessageSquare, Heart, Eye, Repeat2,
  AtSign, CheckCheck, Megaphone, Sparkles,
} from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";

interface CommandCentreDashboardProps {
  onNavigate: (view: string) => void;
}

const ACTION_META: Record<string, { icon: typeof Ticket; label: string; colour: string; bg: string }> = {
  press_pass_issued:      { icon: Ticket,      label: "Press Pass",       colour: "text-[#006B3C]",  bg: "bg-[#006B3C]/10" },
  accreditation_approved: { icon: FileCheck,   label: "Accreditation",    colour: "text-[#006B3C]",  bg: "bg-[#006B3C]/10" },
  event_created:          { icon: CalendarDays, label: "Event",           colour: "text-[#0D2240]",  bg: "bg-[#0D2240]/10" },
  media_kit_uploaded:     { icon: FileUp,      label: "Media Kit",        colour: "text-[#C8A84B]",  bg: "bg-[#C8A84B]/10" },
  article_submitted:      { icon: Newspaper,   label: "Article",          colour: "text-purple-600", bg: "bg-purple-100" },
  award_issued:           { icon: Award,       label: "Award",            colour: "text-[#C8A84B]",  bg: "bg-[#C8A84B]/10" },
};

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  instagram: Instagram, twitter: Twitter, linkedin: Linkedin,
  facebook: Facebook, threads: AtSign,
};

const PLATFORM_GRADIENTS: Record<string, string> = {
  instagram: "from-purple-500 to-pink-500",
  twitter:   "from-slate-700 to-slate-900",
  linkedin:  "from-blue-600 to-blue-700",
  facebook:  "from-blue-500 to-blue-600",
  threads:   "from-slate-700 to-slate-900",
};

function formatK(n: number) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function getDayContext() {
  return new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
}

export function CommandCentreDashboard({ onNavigate }: CommandCentreDashboardProps) {
  const { orgId } = useOrgPermissions();
  const qc = useQueryClient();

  const { data: journalistStats } = useQuery({
    queryKey: ["dash-journalist-stats", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase.from("org_journalist_stats" as any).select("*").eq("org_id", orgId!).maybeSingle() as any);
      return data as { total_journalists: number; active_journalists: number; countries_reached: number } | null;
    },
  });

  const { data: eventStats } = useQuery({
    queryKey: ["dash-event-stats", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase.from("org_event_stats" as any).select("*").eq("org_id", orgId!).maybeSingle() as any);
      return data as { total_events: number; upcoming_events: number; events_this_week: number } | null;
    },
  });

  const { data: coverageStats } = useQuery({
    queryKey: ["dash-coverage-stats", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase.from("org_coverage_stats" as any).select("*").eq("org_id", orgId!).maybeSingle() as any);
      return data as { total_articles: number; avg_score: number; top_score: number; articles_this_week: number } | null;
    },
  });

  const { data: pendingActions } = useQuery({
    queryKey: ["dash-pending-actions", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase.from("org_pending_actions" as any).select("*") as any);
      return (data || []) as { action_type: string; label: string; count: number; navigate_to: string }[];
    },
  });

  const { data: activity, refetch: refetchActivity } = useQuery({
    queryKey: ["dash-activity", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await supabase
        .from("activity_log").select("*").eq("org_id", orgId!)
        .order("created_at", { ascending: false }).limit(10);
      return (data || []) as { id: string; actor_name: string; action_type: string; description: string; created_at: string }[];
    },
  });

  const { data: coveragePulse } = useQuery({
    queryKey: ["dash-coverage-pulse", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase.from("coverage_articles").select("submitted_at").eq("org_id", orgId!).gte("submitted_at", sevenDaysAgo);
      const byDay: Record<string, number> = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        byDay[d.toISOString().slice(0, 10)] = 0;
      }
      (data || []).forEach((row: any) => { const day = (row.submitted_at as string).slice(0, 10); if (day in byDay) byDay[day]++; });
      return Object.entries(byDay).map(([day, count]) => ({ day, count }));
    },
  });

  // ── Social media data ─────────────────────────────────────────────────────
  const { data: socialAccounts = [] } = useQuery({
    queryKey: ["dash-social-accounts", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      let q = supabase.from("social_accounts").select("id,platform,account_name,account_handle,follower_count,last_synced_at,token_expires_at").eq("user_id", user.id).eq("is_connected", true);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      return (data ?? []) as any[];
    },
  });

  const { data: socialPostStats } = useQuery({
    queryKey: ["dash-social-post-stats", orgId],
    queryFn: async () => {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      let q = supabase.from("posts").select("id,status,created_at", { count: "exact" });
      if (orgId) q = (q as any).eq("org_id", orgId);
      const [scheduled, published, pendingApproval, thisWeek] = await Promise.all([
        (async () => { const { count } = await (q as any).eq("status", "scheduled"); return count ?? 0; })(),
        (async () => { const { count } = await (q as any).eq("status", "published"); return count ?? 0; })(),
        (async () => { const { count } = await (q as any).eq("status", "pending_approval"); return count ?? 0; })(),
        (async () => { const { count } = await (q as any).eq("status", "published").gte("created_at", sevenDaysAgo); return count ?? 0; })(),
      ]);
      return { scheduled, published, pendingApproval, thisWeek };
    },
  });

  const { data: socialPulse } = useQuery({
    queryKey: ["dash-social-pulse", orgId],
    queryFn: async () => {
      const byDay: Record<string, number> = {};
      for (let i = 6; i >= 0; i--) {
        const d = new Date(); d.setDate(d.getDate() - i);
        byDay[d.toISOString().slice(0, 10)] = 0;
      }
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      let q = supabase.from("posts").select("created_at").eq("status", "published").gte("created_at", sevenDaysAgo);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      (data || []).forEach((row: any) => { const day = (row.created_at as string).slice(0, 10); if (day in byDay) byDay[day]++; });
      return Object.entries(byDay).map(([day, count]) => ({ day, count }));
    },
  });

  const { data: recentPosts = [] } = useQuery({
    queryKey: ["dash-recent-posts", orgId],
    queryFn: async () => {
      let q = supabase.from("posts").select("id,content,status,platform,created_at,published_at").order("created_at", { ascending: false }).limit(5);
      if (orgId) q = (q as any).eq("org_id", orgId);
      const { data } = await (q as any);
      return (data ?? []) as any[];
    },
  });

  useEffect(() => {
    if (!orgId) return;
    const channel = supabase.channel(`dash-realtime-${orgId}`)
      // Press activity
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "activity_log", filter: `org_id=eq.${orgId}` },
        () => {
          refetchActivity();
          qc.invalidateQueries({ queryKey: ["dash-pending-actions", orgId] });
        })
      // Social accounts updated (sync, connect, disconnect)
      .on("postgres_changes", { event: "*", schema: "public", table: "social_accounts" },
        () => {
          qc.invalidateQueries({ queryKey: ["dash-social-accounts", orgId] });
        })
      // Posts created / status changed
      .on("postgres_changes", { event: "*", schema: "public", table: "posts" },
        () => {
          qc.invalidateQueries({ queryKey: ["dash-social-post-stats", orgId] });
          qc.invalidateQueries({ queryKey: ["dash-social-pulse", orgId] });
          qc.invalidateQueries({ queryKey: ["dash-recent-posts", orgId] });
        })
      // Post analytics synced
      .on("postgres_changes", { event: "*", schema: "public", table: "post_analytics" },
        () => {
          qc.invalidateQueries({ queryKey: ["dash-social-post-stats", orgId] });
        })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [orgId, qc, refetchActivity]);

  const maxCovPulse = Math.max(1, ...(coveragePulse || []).map(d => d.count));
  const maxSocPulse = Math.max(1, ...(socialPulse || []).map(d => d.count));
  const totalCovPulse = (coveragePulse || []).reduce((s, d) => s + d.count, 0);
  const totalSocPulse = (socialPulse || []).reduce((s, d) => s + d.count, 0);
  const pendingCount = (pendingActions || []).reduce((s, a) => s + (a.count || 1), 0);
  const totalFollowers = socialAccounts.reduce((s: number, a: any) => s + (a.follower_count ?? 0), 0);
  const avgScore = coverageStats?.avg_score ?? 0;
  const scoreHealth = avgScore >= 700 ? "excellent" : avgScore >= 500 ? "good" : avgScore >= 300 ? "fair" : "low";
  const scoreColour = { excellent: "#006B3C", good: "#C8A84B", fair: "#f97316", low: "#ef4444" }[scoreHealth];

  return (
    <div className="space-y-6">

      {/* ── Hero banner ───────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0D2240] via-[#0D2240] to-[#006B3C] p-6 text-white">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle at 80% 20%, #C8A84B 0%, transparent 50%), radial-gradient(circle at 20% 80%, #006B3C 0%, transparent 50%)" }} />
        <div className="relative">
          <div className="flex items-start justify-between gap-4 mb-5">
            <div>
              <p className="text-white/50 text-xs font-medium uppercase tracking-widest mb-1">{getDayContext()}</p>
              <h2 className="text-2xl font-bold mb-1">{getGreeting()} 👋</h2>
              <p className="text-white/60 text-sm max-w-xl">
                {pendingCount > 0
                  ? `You have ${pendingCount} item${pendingCount !== 1 ? "s" : ""} awaiting attention.`
                  : "Everything is running smoothly — nothing urgent pending."}
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-1 text-[10px] text-green-400 font-medium">
              <Radio className="w-2.5 h-2.5 animate-pulse" /> LIVE
            </div>
          </div>

          {/* Dual stats: press + social */}
          <div className="grid grid-cols-2 gap-3">
            {/* Press stats */}
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold flex items-center gap-1.5">
                <Newspaper className="w-3 h-3" /> Press Operations
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Journalists", value: journalistStats?.total_journalists ?? 0, icon: Users },
                  { label: "Events / week", value: eventStats?.events_this_week ?? 0, icon: CalendarDays },
                  { label: "Articles / week", value: coverageStats?.articles_this_week ?? 0, icon: BarChart3 },
                  { label: "Countries", value: journalistStats?.countries_reached ?? 0, icon: Globe2 },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="bg-white/10 backdrop-blur-sm rounded-lg px-2.5 py-2 flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-white/50 shrink-0" />
                    <div>
                      <p className="text-base font-bold leading-none">{value}</p>
                      <p className="text-[10px] text-white/40 mt-0.5">{label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Social stats */}
            <div className="space-y-2">
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold flex items-center gap-1.5">
                <Share2 className="w-3 h-3" /> Social Media
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Platforms", value: socialAccounts.length, icon: Share2 },
                  { label: "Total followers", value: formatK(totalFollowers), icon: Users },
                  { label: "Scheduled", value: socialPostStats?.scheduled ?? 0, icon: Clock },
                  { label: "Pending approval", value: socialPostStats?.pendingApproval ?? 0, icon: CheckCheck },
                ].map(({ label, value, icon: Icon }) => (
                  <div key={label} className="bg-white/10 backdrop-blur-sm rounded-lg px-2.5 py-2 flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5 text-white/50 shrink-0" />
                    <div>
                      <p className="text-base font-bold leading-none">{value}</p>
                      <p className="text-[10px] text-white/40 mt-0.5">{label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Primary metric cards: 4 press + 4 social ──────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Journalist Registry" primary={String(journalistStats?.total_journalists ?? 0)} secondary={`${journalistStats?.active_journalists ?? 0} active`} trend="positive" icon={Users} accent="#006B3C" onClick={() => onNavigate("media-registry")} />
        <StatCard title="Upcoming Events"    primary={String(eventStats?.upcoming_events ?? 0)}       secondary={`${eventStats?.events_this_week ?? 0} this week`} trend="neutral" icon={CalendarDays} accent="#0D2240" onClick={() => onNavigate("events")} />
        <StatCard title="Coverage Articles"  primary={String(coverageStats?.total_articles ?? 0)}     secondary={`${coverageStats?.articles_this_week ?? 0} new this week`} trend={coverageStats?.articles_this_week ? "positive" : "neutral"} icon={BarChart3} accent="#7c3aed" onClick={() => onNavigate("analytics")} />
        <StatCard title="Pending Actions"    primary={String(pendingCount)} secondary={pendingCount > 0 ? "needs attention" : "all clear"} trend={pendingCount > 0 ? "negative" : "positive"} icon={FileCheck} accent={pendingCount > 0 ? "#ef4444" : "#006B3C"} onClick={() => onNavigate("accreditation")} />

        <StatCard title="Total Followers"    primary={formatK(totalFollowers)} secondary={`${socialAccounts.length} platform${socialAccounts.length !== 1 ? "s" : ""} connected`} trend="positive" icon={Users} accent="#8b5cf6" onClick={() => onNavigate("social-hub")} />
        <StatCard title="Published Posts"    primary={String(socialPostStats?.published ?? 0)}        secondary={`${socialPostStats?.thisWeek ?? 0} this week`} trend={socialPostStats?.thisWeek ? "positive" : "neutral"} icon={PenSquare} accent="#06b6d4" onClick={() => onNavigate("social-hub")} />
        <StatCard title="Scheduled Posts"    primary={String(socialPostStats?.scheduled ?? 0)}        secondary="ready to go" trend="positive" icon={Clock} accent="#f59e0b" onClick={() => onNavigate("social-hub")} />
        <StatCard title="Pending Approvals"  primary={String(socialPostStats?.pendingApproval ?? 0)}  secondary={socialPostStats?.pendingApproval ? "review required" : "none pending"} trend={socialPostStats?.pendingApproval ? "negative" : "positive"} icon={CheckCheck} accent={socialPostStats?.pendingApproval ? "#ef4444" : "#006B3C"} onClick={() => onNavigate("approvals")} />
      </div>

      {/* ── Social accounts strip ──────────────────────────────────────────── */}
      {socialAccounts.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Share2 className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold">Connected Social Platforms</h3>
            </div>
            <Button size="sm" variant="outline" onClick={() => onNavigate("social-hub")}>
              Open Social Hub <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {socialAccounts.map((acc: any) => {
              const Icon = PLATFORM_ICONS[acc.platform] ?? AtSign;
              const gradient = PLATFORM_GRADIENTS[acc.platform] ?? "from-slate-500 to-slate-600";
              const isExpired = acc.token_expires_at ? new Date(acc.token_expires_at) < new Date() : false;
              return (
                <button
                  key={acc.id}
                  onClick={() => onNavigate("social-hub")}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-muted/40 transition-all group"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-sm group-hover:scale-105 transition-transform`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-center">
                    <p className="text-xs font-semibold capitalize">{acc.platform}</p>
                    <p className="text-[10px] text-muted-foreground">{formatK(acc.follower_count ?? 0)} followers</p>
                  </div>
                  {isExpired && <Badge variant="destructive" className="text-[9px] h-4 px-1">Expired</Badge>}
                </button>
              );
            })}
            <button
              onClick={() => onNavigate("social-hub")}
              className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl border border-dashed border-border hover:border-primary/40 hover:bg-muted/40 transition-all text-muted-foreground hover:text-primary"
            >
              <Sparkles className="w-5 h-5" />
              <p className="text-[10px] font-medium">Social Hub</p>
            </button>
          </div>
        </Card>
      )}

      {/* ── Dual pulse charts ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Social posts pulse */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold">Social Posts Published</h3>
              <p className="text-xs text-muted-foreground">Posts published over the last 7 days</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-purple-600">{totalSocPulse}</p>
              <p className="text-xs text-muted-foreground">total this week</p>
            </div>
          </div>
          <MiniBarChart data={socialPulse || []} max={maxSocPulse} colour="#8b5cf6" />
          <div className="mt-3 flex gap-3">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => onNavigate("social-hub")}>
              <PenSquare className="w-3 h-3 mr-1" /> Compose
            </Button>
            <Button size="sm" variant="outline" className="text-xs" onClick={() => onNavigate("calendar")}>
              <CalendarDays className="w-3 h-3 mr-1" /> Calendar
            </Button>
          </div>
        </Card>

        {/* Coverage pulse */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold">Coverage Pulse</h3>
              <p className="text-xs text-muted-foreground">Article submissions over the last 7 days</p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-[#006B3C]">{totalCovPulse}</p>
              <p className="text-xs text-muted-foreground">total this week</p>
            </div>
          </div>
          <MiniBarChart data={coveragePulse || []} max={maxCovPulse} colour="#006B3C" />
          <div className="mt-3 flex gap-3">
            <Button size="sm" variant="outline" className="text-xs" onClick={() => onNavigate("analytics")}>
              <BarChart3 className="w-3 h-3 mr-1" /> Analytics
            </Button>
          </div>
        </Card>
      </div>

      {/* ── Main grid: activity + pending + recent social posts ───────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity feed */}
        <Card className="lg:col-span-2 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-muted-foreground" />
              <h3 className="text-sm font-semibold">Live Activity Feed</h3>
              <span className="flex items-center gap-1 text-[10px] text-green-600 font-medium">
                <Radio className="w-2.5 h-2.5 animate-pulse" /> LIVE
              </span>
            </div>
            <Badge variant="secondary" className="text-xs">{(activity || []).length} recent</Badge>
          </div>
          <div className="space-y-1">
            {(activity || []).length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Activity className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No activity yet — things will appear here as your team works.</p>
              </div>
            )}
            {(activity || []).map((item, i) => (
              <ActivityFeedItem key={item.id} item={item} isNew={i === 0} />
            ))}
          </div>
        </Card>

        {/* Right: pending actions + quick actions */}
        <div className="space-y-4">
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Zap className="w-4 h-4 text-[#C8A84B]" />
              <h3 className="text-sm font-semibold">Action Required</h3>
              {pendingCount > 0 && (
                <Badge className="ml-auto bg-red-100 text-red-700 border-red-200 text-xs">{pendingCount}</Badge>
              )}
            </div>
            {socialPostStats?.pendingApproval ? (
              <PendingActionItem
                action={{ action_type: "social_approval", label: "Social posts to approve", count: socialPostStats.pendingApproval, navigate_to: "approvals" }}
                onNavigate={onNavigate}
              />
            ) : null}
            <div className="space-y-2 mt-2">
              {(pendingActions || []).length === 0 && !socialPostStats?.pendingApproval && (
                <div className="text-center py-4">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                    <FileCheck className="w-4 h-4 text-green-600" />
                  </div>
                  <p className="text-xs text-muted-foreground">All clear — nothing pending.</p>
                </div>
              )}
              {(pendingActions || []).map((action) => (
                <PendingActionItem key={action.action_type} action={action} onNavigate={onNavigate} />
              ))}
            </div>
          </Card>

          {/* Quick actions: press + social */}
          <Card className="p-5">
            <h3 className="text-sm font-semibold mb-3">Quick Actions</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Compose Post",  view: "social-hub",       icon: PenSquare,    colour: "text-purple-600",  bg: "bg-purple-100" },
                { label: "Social Hub",    view: "social-hub",       icon: Share2,       colour: "text-blue-600",    bg: "bg-blue-100" },
                { label: "New Event",     view: "events",           icon: CalendarDays, colour: "text-[#0D2240]",   bg: "bg-[#0D2240]/10" },
                { label: "Analytics",    view: "analytics",        icon: BarChart3,    colour: "text-[#C8A84B]",   bg: "bg-[#C8A84B]/10" },
                { label: "Campaigns",    view: "campaigns",        icon: Megaphone,    colour: "text-rose-600",    bg: "bg-rose-100" },
                { label: "Messages",     view: "social-dms",       icon: MessageSquare,colour: "text-emerald-600", bg: "bg-emerald-100" },
              ].map(({ label, view, icon: Icon, colour, bg }) => (
                <button key={`${label}-${view}`} onClick={() => onNavigate(view)}
                  className="flex flex-col items-center gap-1.5 p-2.5 rounded-lg border border-border hover:border-primary/40 hover:bg-muted/40 transition-colors text-center group">
                  <div className={`w-7 h-7 rounded-lg ${bg} flex items-center justify-center`}>
                    <Icon className={`w-3.5 h-3.5 ${colour}`} />
                  </div>
                  <span className="text-[11px] font-medium leading-tight">{label}</span>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* ── Recent social posts ────────────────────────────────────────────── */}
      {recentPosts.length > 0 && (
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <PenSquare className="w-4 h-4 text-primary" />
              <h3 className="text-sm font-semibold">Recent Social Posts</h3>
            </div>
            <Button size="sm" variant="outline" onClick={() => onNavigate("social-hub")}>
              View all <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          <div className="space-y-2">
            {recentPosts.map((post: any) => {
              const statusMeta: Record<string, { label: string; cls: string }> = {
                published:        { label: "Published", cls: "bg-green-100 text-green-700" },
                scheduled:        { label: "Scheduled", cls: "bg-amber-100 text-amber-700" },
                pending_approval: { label: "Pending",   cls: "bg-red-100 text-red-700" },
                draft:            { label: "Draft",     cls: "bg-gray-100 text-gray-600" },
              };
              const meta = statusMeta[post.status] ?? statusMeta.draft;
              return (
                <div key={post.id} className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/30 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                    <PenSquare className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm line-clamp-2 leading-snug">{post.content}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                      {post.platform && (
                        <span className="text-[10px] text-muted-foreground capitalize">{post.platform}</span>
                      )}
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        <FormattedDate date={post.created_at} />
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ── Coverage score health ─────────────────────────────────────────── */}
      {coverageStats && (
        <Card className="p-5 border-l-4" style={{ borderLeftColor: scoreColour }}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: `${scoreColour}20` }}>
                <Star className="w-5 h-5" style={{ color: scoreColour }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold">Coverage Health</p>
                  <Badge variant="secondary" className="text-xs capitalize" style={{ background: `${scoreColour}20`, color: scoreColour }}>
                    {scoreHealth}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Average score <strong>{avgScore.toFixed(0)}</strong>/1000 · Top score <strong>{coverageStats.top_score ?? 0}</strong>
                </p>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => onNavigate("analytics")}>
              View Coverage <ArrowUpRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </Card>
      )}

      {/* ── Website analytics + assigned posts ───────────────────────────── */}
      <WebsiteAnalyticsWidget onNavigate={onNavigate} />
      <AssignedPostsWidget onNavigate={onNavigate} />
    </div>
  );
}

// ─── Shared mini components ───────────────────────────────────────────────────

function MiniBarChart({ data, max, colour }: { data: { day: string; count: number }[]; max: number; colour: string }) {
  return (
    <div className="flex items-end gap-1.5 h-28">
      {data.map((d, i) => {
        const pct = Math.max(4, (d.count / max) * 100);
        const isToday = i === data.length - 1;
        return (
          <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="w-full flex flex-col items-center justify-end" style={{ height: "88px" }}>
              {d.count > 0 && (
                <span className="text-[10px] text-muted-foreground mb-1 opacity-0 group-hover:opacity-100 transition-opacity">{d.count}</span>
              )}
              <div
                className="w-full rounded-t-sm transition-all"
                style={{
                  height: `${pct}%`,
                  background: isToday ? colour : `${colour}40`,
                }}
              />
            </div>
            <span className="text-[10px]" style={{ color: isToday ? colour : undefined, fontWeight: isToday ? 600 : undefined, opacity: isToday ? 1 : 0.5 }}>
              {new Date(d.day).toLocaleDateString(undefined, { weekday: "short" })}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ title, primary, secondary, trend, icon: Icon, accent, onClick }: {
  title: string; primary: string; secondary: string;
  trend: "positive" | "negative" | "neutral";
  icon: any; accent: string; onClick?: () => void;
}) {
  const TrendIcon = trend === "positive" ? TrendingUp : trend === "negative" ? TrendingDown : Minus;
  const trendColour = trend === "positive" ? "text-green-600" : trend === "negative" ? "text-red-500" : "text-muted-foreground";
  return (
    <button onClick={onClick} className="text-left w-full rounded-xl border border-border bg-card p-4 hover:shadow-md transition-all hover:border-primary/30">
      <div className="flex items-start justify-between mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accent}18` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
        <TrendIcon className={`w-4 h-4 ${trendColour}`} />
      </div>
      <p className="text-2xl font-bold tracking-tight">{primary}</p>
      <p className="text-xs font-medium text-muted-foreground mt-0.5">{title}</p>
      <p className={`text-xs ${trendColour} mt-0.5`}>{secondary}</p>
    </button>
  );
}

function ActivityFeedItem({ item, isNew }: {
  item: { actor_name: string; action_type: string; description: string; created_at: string };
  isNew?: boolean;
}) {
  const meta = ACTION_META[item.action_type] ?? { icon: Trophy, colour: "text-muted-foreground", bg: "bg-muted", label: "Event" };
  const Icon = meta.icon;
  return (
    <div className={`flex items-start gap-3 p-2.5 rounded-lg transition-colors ${isNew ? "bg-primary/5 border border-primary/10" : "hover:bg-muted/40"}`}>
      <div className={`w-7 h-7 rounded-full ${meta.bg} flex items-center justify-center shrink-0 mt-0.5`}>
        <Icon className={`w-3.5 h-3.5 ${meta.colour}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm leading-snug">{item.description}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-muted-foreground">{item.actor_name}</span>
          <span className="text-muted-foreground/40">·</span>
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="w-3 h-3" /><FormattedDate date={item.created_at} showTime />
          </span>
          {isNew && <Badge className="ml-auto text-[10px] bg-primary/10 text-primary border-primary/20">New</Badge>}
        </div>
      </div>
    </div>
  );
}

function PendingActionItem({ action, onNavigate }: {
  action: { action_type: string; label: string; count: number; navigate_to: string };
  onNavigate: (view: string) => void;
}) {
  return (
    <div onClick={() => onNavigate(action.navigate_to)}
      className="flex items-center justify-between gap-2 py-2.5 px-3 rounded-lg border border-border hover:border-primary/40 hover:bg-muted/40 cursor-pointer transition-colors">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{action.label}</p>
        {action.count > 0 && <p className="text-xs text-muted-foreground">{action.count} item{action.count !== 1 ? "s" : ""} pending</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {action.count > 0 && (
          <span className="w-5 h-5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold flex items-center justify-center">{action.count}</span>
        )}
        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
    </div>
  );
}
